import React, { useEffect, useState, useRef } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { BookOpenIcon, CheckIcon, ClockIcon, AwardIcon, UsersIcon, BuildingIcon, ShieldCheckIcon, BarChartIcon, FileTextIcon, DownloadIcon, ChevronRightIcon, ChevronDownIcon, StarIcon, CheckCircleIcon, BadgeCheckIcon, ArrowRightIcon, PlusIcon, MinusIcon, UserIcon, LightbulbIcon, AlertCircleIcon, UtensilsIcon, ShieldIcon, MapPinIcon, ScrollIcon, CalendarIcon, MessageCircleIcon, HeartIcon, PhoneIcon, CreditCardIcon } from 'lucide-react';
export const HACCPLevel1Page = () => {
  return <div className="flex flex-col min-h-screen w-full bg-white text-charcoal" data-id="element-2291">
      <Header data-id="element-2292" />
      <main className="flex-grow" data-id="element-2293">
        <HeroSection data-id="element-2294" />
        <TrustBanner data-id="element-2295" />
        <WhyTakeTrainingSection data-id="element-2296" />
        <WhoShouldTakeSection data-id="element-2297" />
        <LearningOutcomesSection data-id="element-2298" />
        <CourseFormatSection data-id="element-2299" />
        <CertificationSection data-id="element-2300" />
        <TrainingOptionsSection data-id="element-2301" />
        <WhyChooseSection data-id="element-2302" />
        <FaqSection data-id="element-2303" />
        <CtaSection data-id="element-2304" />
      </main>
      <Footer data-id="element-2305" />
    </div>;
};
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    setIsVisible(true);
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden" data-id="element-2306">
      {/* Background with enhanced overlay */}
      <div className="absolute inset-0 z-0" data-id="element-2307">
        <img src="https://images.unsplash.com/photo-1574484284002-952d92456975?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Professional Kitchen Environment in Ireland" className="w-full h-full object-cover" data-id="element-2308" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-charcoal/85 to-charcoal/75" data-id="element-2309"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-soft-light" data-id="element-2310"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2311">
        <div className="max-w-3xl" data-id="element-2312">
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-2313">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-teal/20 backdrop-blur-sm border border-teal/30 mb-6" data-id="element-2314">
              <MapPinIcon className="h-4 w-4 text-aqua mr-2" data-id="element-2315" />
              <span className="text-white/90 text-sm font-medium" data-id="element-2316">
                FSAI Compliant Training for Ireland
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight" data-id="element-2317">
              HACCP Level 1{' '}
              <span className="text-gradient bg-gradient-to-r from-teal to-aqua bg-clip-text text-transparent" data-id="element-2318">
                Food Safety Training
              </span>
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed" data-id="element-2319">
              Kivarro's HACCP Level 1 Food Safety Training Ireland is the
              mandatory induction course for all new food handlers. Fully
              aligned with the Food Safety Authority of Ireland (FSAI)
              framework, this accredited online food safety course gives staff
              the essential knowledge to work safely, protect customers, and
              meet legal food hygiene requirements.
            </p>
            <div className="flex items-center gap-6 mb-8" data-id="element-2320">
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg" data-id="element-2321">
                <ClockIcon className="h-5 w-5 text-aqua mr-3 flex-shrink-0" data-id="element-2322" />
                <span className="text-white" data-id="element-2323">Complete in 60 Minutes</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg" data-id="element-2324">
                <AwardIcon className="h-5 w-5 text-aqua mr-3 flex-shrink-0" data-id="element-2325" />
                <span className="text-white" data-id="element-2326">FSAI Compliant</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-2327">
              <a href="#individual-enrollment" className="px-6 py-3.5 bg-teal text-white font-medium rounded-lg hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 text-center shadow-lg flex items-center justify-center" data-id="element-2328">
                Enroll Now
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-2329" />
              </a>
              <a href="#group-enrollment" className="px-6 py-3.5 bg-transparent text-white font-medium rounded-lg border-2 border-white hover:bg-white/10 transition-all duration-300 text-center flex items-center justify-center" data-id="element-2330">
                <BuildingIcon className="mr-2 h-5 w-5" data-id="element-2331" />
                Group Booking
              </a>
            </div>
            {/* Money Back Guarantee Banner */}
            <div className="bg-gradient-to-r from-amber-400/90 to-amber-500/90 rounded-lg p-4 shadow-lg border border-amber-300" data-id="element-2332">
              <div className="flex items-center" data-id="element-2333">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2334">
                  <ShieldIcon className="h-6 w-6 text-white" data-id="element-2335" />
                </div>
                <div data-id="element-2336">
                  <h3 className="font-bold text-white text-lg" data-id="element-2337">
                    100% Acceptance Guarantee
                  </h3>
                  <p className="text-white/90" data-id="element-2338">
                    If your certificate is not accepted by regulators, we'll
                    refund your purchase in full.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const TrustBanner = () => {
  return <section className="py-4 bg-gray-50 border-y border-gray-200" data-id="element-2339">
      <div className="container mx-auto px-4" data-id="element-2340">
        <div className="flex flex-wrap items-center justify-center md:justify-between gap-6 text-center md:text-left" data-id="element-2341">
          <p className="text-charcoal/70 font-medium" data-id="element-2342">
            Trusted by leading food businesses across Ireland:
          </p>
          <div className="flex flex-wrap justify-center gap-8" data-id="element-2343">
            <div className="text-charcoal/80 font-semibold" data-id="element-2344">Dunnes Stores</div>
            <div className="text-charcoal/80 font-semibold" data-id="element-2345">SuperValu</div>
            <div className="text-charcoal/80 font-semibold" data-id="element-2346">Avoca</div>
            <div className="text-charcoal/80 font-semibold" data-id="element-2347">Musgrave Group</div>
          </div>
        </div>
      </div>
    </section>;
};
const WhyTakeTrainingSection = () => {
  return <section className="py-20 bg-white" data-id="element-2348">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2349">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2350">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2351">
            <LightbulbIcon className="h-6 w-6 text-teal" data-id="element-2352" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal" data-id="element-2353">
            Why Take This Food Safety Training?
          </h2>
          <p className="text-xl text-charcoal/70" data-id="element-2354">
            Essential training for all food handlers in Ireland with
            industry-recognized benefits
          </p>
        </div>
        <div className="max-w-4xl mx-auto" data-id="element-2355">
          <div className="prose prose-lg max-w-none" data-id="element-2356">
            <div className="mb-12" data-id="element-2357">
              <h3 className="text-2xl font-bold text-charcoal mb-6 flex items-center" data-id="element-2358">
                <ScrollIcon className="h-6 w-6 text-teal mr-3 flex-shrink-0" data-id="element-2359" />
                Legal Requirements & Compliance
              </h3>
              <ul className="space-y-4" data-id="element-2360">
                <li className="flex items-start" data-id="element-2361">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2362" />
                  <div data-id="element-2363">
                    <span className="font-semibold" data-id="element-2364">Required by Law:</span>{' '}
                    Mandatory under Irish food safety law for all food handlers
                    working in Ireland.
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2365">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2366" />
                  <div data-id="element-2367">
                    <span className="font-semibold" data-id="element-2368">FSAI Compliant:</span> Fully
                    covers FSAI Level 1 induction requirements (Stage I & Stage
                    II).
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2369">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2370" />
                  <div data-id="element-2371">
                    <span className="font-semibold" data-id="element-2372">
                      Staged Training Approach:
                    </span>{' '}
                    Stage I must be completed before handling food, Stage II
                    within the first month of employment.
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2373">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2374" />
                  <div data-id="element-2375">
                    <span className="font-semibold" data-id="element-2376">Industry Recognition:</span>{' '}
                    Accredited and recognized by Irish employers, Environmental
                    Health Officers (EHOs), and food safety inspectors.
                  </div>
                </li>
              </ul>
            </div>
            <div data-id="element-2377">
              <h3 className="text-2xl font-bold text-charcoal mb-6 flex items-center" data-id="element-2378">
                <ClockIcon className="h-6 w-6 text-teal mr-3 flex-shrink-0" data-id="element-2379" />
                Practical Benefits
              </h3>
              <ul className="space-y-4" data-id="element-2380">
                <li className="flex items-start" data-id="element-2381">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2382" />
                  <div data-id="element-2383">
                    <span className="font-semibold" data-id="element-2384">Fast Completion:</span>{' '}
                    Complete the 60-minute course online at your own pace,
                    available on any device.
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2385">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2386" />
                  <div data-id="element-2387">
                    <span className="font-semibold" data-id="element-2388">
                      Instant Certification:
                    </span>{' '}
                    Downloadable HACCP certificate as PDF immediately upon
                    successful completion.
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2389">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2390" />
                  <div data-id="element-2391">
                    <span className="font-semibold" data-id="element-2392">
                      Unlimited Exam Attempts:
                    </span>{' '}
                    Take the final assessment as many times as needed until you
                    pass.
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2393">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2394" />
                  <div data-id="element-2395">
                    <span className="font-semibold" data-id="element-2396">Flexible Options:</span>{' '}
                    Available for individual learners or as group bookings for
                    businesses.
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const WhoShouldTakeSection = () => {
  return <section className="py-20 bg-gray-50" data-id="element-2397">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2398">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2399">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2400">
            <UsersIcon className="h-6 w-6 text-teal" data-id="element-2401" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal" data-id="element-2402">
            Who Should Take This Course?
          </h2>
        </div>
        <div className="max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-md" data-id="element-2403">
          <p className="text-lg text-charcoal/80 mb-6" data-id="element-2404">
            The HACCP Level 1 Food Safety Course is designed for all new
            employees in Irish food businesses. It is required for:
          </p>
          <ul className="space-y-4 mb-8" data-id="element-2405">
            <li className="flex items-start" data-id="element-2406">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2407" />
              <span className="text-charcoal/80" data-id="element-2408">
                Kitchen porters, catering assistants, deli staff, wait staff,
                baristas.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2409">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2410" />
              <span className="text-charcoal/80" data-id="element-2411">
                Employees in restaurants, cafés, hotels, bars, takeaways,
                catering companies, food retail, and food production facilities.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2412">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2413" />
              <span className="text-charcoal/80" data-id="element-2414">
                Any new food handler who must complete induction training before
                or shortly after starting work.
              </span>
            </li>
          </ul>
          <div className="bg-teal/5 rounded-lg p-5 border border-teal/10 mb-6" data-id="element-2415">
            <p className="text-charcoal/80" data-id="element-2416">
              This training ensures staff complete both Stage I (before handling
              food) and Stage II (within one month), as required by the FSAI.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-2417">
            <div className="bg-teal/5 rounded-lg p-5 border border-teal/10" data-id="element-2418">
              <div className="flex items-center mb-3" data-id="element-2419">
                <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-2420">
                  <span className="font-bold text-teal" data-id="element-2421">I</span>
                </div>
                <h4 className="text-lg font-bold text-charcoal" data-id="element-2422">Stage I</h4>
              </div>
              <p className="text-charcoal/70" data-id="element-2423">
                Must be completed{' '}
                <span className="font-semibold text-teal" data-id="element-2424">before</span> an
                employee begins handling food.
              </p>
            </div>
            <div className="bg-teal/5 rounded-lg p-5 border border-teal/10" data-id="element-2425">
              <div className="flex items-center mb-3" data-id="element-2426">
                <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-2427">
                  <span className="font-bold text-teal" data-id="element-2428">II</span>
                </div>
                <h4 className="text-lg font-bold text-charcoal" data-id="element-2429">Stage II</h4>
              </div>
              <p className="text-charcoal/70" data-id="element-2430">
                Must be completed{' '}
                <span className="font-semibold text-teal" data-id="element-2431">
                  within the first month
                </span>{' '}
                of employment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const LearningOutcomesSection = () => {
  return <section className="py-20 bg-white" data-id="element-2432">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2433">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2434">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2435">
            <BookOpenIcon className="h-6 w-6 text-teal" data-id="element-2436" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal" data-id="element-2437">
            What You'll Learn in HACCP Level 1
          </h2>
        </div>
        <div className="max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-md" data-id="element-2438">
          <p className="text-lg text-charcoal/80 mb-6" data-id="element-2439">
            By completing this food safety training course Ireland, learners
            will gain the knowledge to:
          </p>
          <ul className="space-y-4" data-id="element-2440">
            <li className="flex items-start" data-id="element-2441">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2442" />
              <span className="text-charcoal/80" data-id="element-2443">
                Understand food hygiene principles and legal responsibilities.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2444">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2445" />
              <span className="text-charcoal/80" data-id="element-2446">
                Apply correct handwashing and personal hygiene practices.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2447">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2448" />
              <span className="text-charcoal/80" data-id="element-2449">
                Recognise and prevent contamination and cross-contamination.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2450">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2451" />
              <span className="text-charcoal/80" data-id="element-2452">
                Follow safe procedures for food storage, cooking, and
                temperature control.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2453">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2454" />
              <span className="text-charcoal/80" data-id="element-2455">
                Carry out cleaning and sanitising routines effectively.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2456">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2457" />
              <span className="text-charcoal/80" data-id="element-2458">
                Identify pests, hazards, and risks in the workplace.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2459">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2460" />
              <span className="text-charcoal/80" data-id="element-2461">
                Understand the basics of HACCP (Hazard Analysis and Critical
                Control Points).
              </span>
            </li>
            <li className="flex items-start" data-id="element-2462">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2463" />
              <span className="text-charcoal/80" data-id="element-2464">
                Cooperate with Environmental Health Officers (EHOs) during
                inspections.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </section>;
};
const CourseFormatSection = () => {
  return <section className="py-20 bg-gray-50" data-id="element-2465">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2466">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2467">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2468">
            <FileTextIcon className="h-6 w-6 text-teal" data-id="element-2469" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal" data-id="element-2470">
            Course Format
          </h2>
        </div>
        <div className="max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-md" data-id="element-2471">
          <div className="space-y-6" data-id="element-2472">
            <div className="flex items-start" data-id="element-2473">
              <ClockIcon className="h-6 w-6 text-teal mt-0.5 mr-4 flex-shrink-0" data-id="element-2474" />
              <div data-id="element-2475">
                <h4 className="font-bold text-charcoal mb-1" data-id="element-2476">Duration</h4>
                <p className="text-charcoal/70" data-id="element-2477">Approximately 60 minutes.</p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2478">
              <div className="h-6 w-6 text-teal mt-0.5 mr-4 flex-shrink-0" data-id="element-2479">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2480">
                  <rect x="2" y="4" width="20" height="12" rx="2" ry="2" data-id="element-2481"></rect>
                  <line x1="2" y1="20" x2="22" y2="20" data-id="element-2482"></line>
                </svg>
              </div>
              <div data-id="element-2483">
                <h4 className="font-bold text-charcoal mb-1" data-id="element-2484">Delivery</h4>
                <p className="text-charcoal/70" data-id="element-2485">
                  100% online — available 24/7 on desktop, tablet, or mobile.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2486">
              <BookOpenIcon className="h-6 w-6 text-teal mt-0.5 mr-4 flex-shrink-0" data-id="element-2487" />
              <div data-id="element-2488">
                <h4 className="font-bold text-charcoal mb-1" data-id="element-2489">Modules</h4>
                <p className="text-charcoal/70" data-id="element-2490">
                  Video lessons with interactive checkpoints.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2491">
              <CheckCircleIcon className="h-6 w-6 text-teal mt-0.5 mr-4 flex-shrink-0" data-id="element-2492" />
              <div data-id="element-2493">
                <h4 className="font-bold text-charcoal mb-1" data-id="element-2494">Assessment</h4>
                <p className="text-charcoal/70" data-id="element-2495">
                  Final exam (70% pass mark, unlimited retakes).
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2496">
              <HeartIcon className="h-6 w-6 text-teal mt-0.5 mr-4 flex-shrink-0" data-id="element-2497" />
              <div data-id="element-2498">
                <h4 className="font-bold text-charcoal mb-1" data-id="element-2499">Accessibility</h4>
                <p className="text-charcoal/70" data-id="element-2500">
                  Closed captions included; suitable for ESOL learners.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const CertificationSection = () => {
  return <section className="py-20 bg-white" data-id="element-2501">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2502">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2503">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2504">
            <AwardIcon className="h-6 w-6 text-teal" data-id="element-2505" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal" data-id="element-2506">
            Certification
          </h2>
        </div>
        <div className="max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-md" data-id="element-2507">
          <p className="text-lg text-charcoal/80 mb-6" data-id="element-2508">
            On completion, learners receive a:
          </p>
          <ul className="space-y-4 mb-8" data-id="element-2509">
            <li className="flex items-start" data-id="element-2510">
              <BadgeCheckIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2511" />
              <span className="text-charcoal/80" data-id="element-2512">
                HACCP Level 1 Food Safety Certificate (Ireland).
              </span>
            </li>
            <li className="flex items-start" data-id="element-2513">
              <BadgeCheckIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2514" />
              <span className="text-charcoal/80" data-id="element-2515">
                Fully accredited and aligned with FSAI requirements.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2516">
              <BadgeCheckIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2517" />
              <span className="text-charcoal/80" data-id="element-2518">
                Recognised by Irish employers and EHOs.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2519">
              <BadgeCheckIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2520" />
              <span className="text-charcoal/80" data-id="element-2521">
                Valid for 3 years (refresher training required every 3 years, or
                sooner if procedures or legislation change).
              </span>
            </li>
            <li className="flex items-start" data-id="element-2522">
              <BadgeCheckIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2523" />
              <span className="text-charcoal/80" data-id="element-2524">
                Secure instant PDF certificate downloadable immediately.
              </span>
            </li>
          </ul>
          <div className="flex items-center p-4 bg-teal/10 rounded-lg" data-id="element-2525">
            <DownloadIcon className="h-6 w-6 text-teal mr-4 flex-shrink-0" data-id="element-2526" />
            <p className="text-charcoal/80" data-id="element-2527">
              Your certificate is available for download immediately after
              completion and is valid for 3 years.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
const TrainingOptionsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section id="enrollment-options" ref={sectionRef} className="py-20 bg-gray-50" data-id="element-2528">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2529">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2530">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2531">
            <UsersIcon className="h-6 w-6 text-teal" data-id="element-2532" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal" data-id="element-2533">
            Training Options
          </h2>
        </div>
        <div className="max-w-4xl mx-auto" data-id="element-2534">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2535">
            <div id="individual-enrollment" className={`bg-white rounded-xl shadow-md p-8 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-2536">
              <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-2537">
                For Individual Learners
              </h3>
              <ul className="space-y-4 mb-8" data-id="element-2538">
                <li className="flex items-start" data-id="element-2539">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-0.5 mr-3 flex-shrink-0" data-id="element-2540" />
                  <span className="text-charcoal/80" data-id="element-2541">
                    Enroll online today for instant access.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2542">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-0.5 mr-3 flex-shrink-0" data-id="element-2543" />
                  <span className="text-charcoal/80" data-id="element-2544">
                    Complete training at your own pace.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2545">
                  <CheckCircleIcon className="h-5 w-5 text-teal mt-0.5 mr-3 flex-shrink-0" data-id="element-2546" />
                  <span className="text-charcoal/80" data-id="element-2547">
                    Earn your recognised HACCP Level 1 certificate in under 1
                    hour.
                  </span>
                </li>
              </ul>
              <a href="/checkout/individual" className="block w-full py-3 bg-teal text-white font-medium rounded-lg hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 text-center shadow-md" data-id="element-2548">
                Enroll Now
              </a>
            </div>
            <div id="group-enrollment" className={`bg-charcoal rounded-xl shadow-md p-8 text-white transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} delay-200`} data-id="element-2549">
              <h3 className="text-2xl font-bold mb-6" data-id="element-2550">
                For Businesses & Groups
              </h3>
              <ul className="space-y-4 mb-8" data-id="element-2551">
                <li className="flex items-start" data-id="element-2552">
                  <CheckCircleIcon className="h-5 w-5 text-aqua mt-0.5 mr-3 flex-shrink-0" data-id="element-2553" />
                  <span className="text-white/80" data-id="element-2554">
                    Flexible group booking options for teams of any size.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2555">
                  <CheckCircleIcon className="h-5 w-5 text-aqua mt-0.5 mr-3 flex-shrink-0" data-id="element-2556" />
                  <span className="text-white/80" data-id="element-2557">
                    Bulk enrollments with staff progress tracking.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2558">
                  <CheckCircleIcon className="h-5 w-5 text-aqua mt-0.5 mr-3 flex-shrink-0" data-id="element-2559" />
                  <span className="text-white/80" data-id="element-2560">
                    Centralised reporting for compliance records.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2561">
                  <CheckCircleIcon className="h-5 w-5 text-aqua mt-0.5 mr-3 flex-shrink-0" data-id="element-2562" />
                  <span className="text-white/80" data-id="element-2563">
                    Cost-effective training for restaurants, cafés, hotels,
                    catering companies, and food producers.
                  </span>
                </li>
              </ul>
              <div className="grid grid-cols-2 gap-4" data-id="element-2564">
                <a href="/contact" className="block py-3 bg-transparent text-white font-medium rounded-lg border border-white/30 hover:bg-white/10 transition-all duration-300 text-center flex items-center justify-center" data-id="element-2565">
                  <PhoneIcon className="mr-2 h-5 w-5" data-id="element-2566" />
                  Contact Sales
                </a>
                <a href="/checkout/team" className="block py-3 bg-aqua text-white font-medium rounded-lg hover:bg-aqua/90 transition-all duration-300 text-center shadow-md" data-id="element-2567">
                  Group Booking
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-2568">
            <p className="text-charcoal/70 mb-2" data-id="element-2569">
              👉 Contact us for group rates and tailored compliance packages.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
const WhyChooseSection = () => {
  return <section className="py-20 bg-white" data-id="element-2570">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2571">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2572">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2573">
            <HeartIcon className="h-6 w-6 text-teal" data-id="element-2574" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal" data-id="element-2575">
            Why Choose Kivarro?
          </h2>
        </div>
        <div className="max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-md" data-id="element-2576">
          <ul className="space-y-4" data-id="element-2577">
            <li className="flex items-start" data-id="element-2578">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2579" />
              <span className="text-charcoal/80" data-id="element-2580">
                Irish-based training provider, trusted nationwide.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2581">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2582" />
              <span className="text-charcoal/80" data-id="element-2583">
                Professional, engaging courses designed by compliance experts.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2584">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2585" />
              <span className="text-charcoal/80" data-id="element-2586">
                Fully FSAI-compliant content.
              </span>
            </li>
            <li className="flex items-start" data-id="element-2587">
              <CheckCircleIcon className="h-5 w-5 text-teal mt-1 mr-3 flex-shrink-0" data-id="element-2588" />
              <span className="text-charcoal/80" data-id="element-2589">
                Easy online booking, instant certification, and responsive Irish
                support.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </section>;
};
const FaqSection = () => {
  const [openQuestion, setOpenQuestion] = useState(null);
  const toggleQuestion = questionId => {
    setOpenQuestion(openQuestion === questionId ? null : questionId);
  };
  const faqs = [{
    id: 1,
    question: 'Is HACCP Level 1 training mandatory in Ireland?',
    answer: 'Yes. All food handlers must receive induction training in line with the FSAI framework. Level 1 (Stage I & Stage II) is the mandatory induction training for all new staff.'
  }, {
    id: 2,
    question: 'When must this training be completed?',
    answer: 'Stage I: before handling food. Stage II: within 1 month of starting employment.'
  }, {
    id: 3,
    question: 'How long does the certificate last?',
    answer: 'Certificates are valid for 3 years. Refresher training is required at least every 3 years, or sooner if food safety law, workplace practices, or your role changes.'
  }, {
    id: 4,
    question: 'Is the course fully online?',
    answer: "Yes. It's delivered 100% online with video modules, quizzes, and a final exam. Accessible on desktop, tablet, and mobile."
  }, {
    id: 5,
    question: 'Is the certificate recognised?',
    answer: "Yes. It's fully aligned with the Food Safety Authority of Ireland (FSAI) and recognised by employers and EHOs."
  }, {
    id: 6,
    question: 'Do you offer discounts for groups?',
    answer: 'Yes. We provide business packages with bulk enrollments, progress tracking, and centralized reporting.'
  }];
  return <section className="py-20 bg-gray-50" data-id="element-2590">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2591">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2592">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-2593">
            <ChevronDownIcon className="h-6 w-6 text-teal" data-id="element-2594" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal" data-id="element-2595">
            FAQs – Food Safety Training Ireland
          </h2>
        </div>
        <div className="max-w-4xl mx-auto" data-id="element-2596">
          {faqs.map(faq => <div key={faq.id} className="mb-4 border border-gray-200 rounded-xl overflow-hidden" data-id="element-2597">
              <button onClick={() => toggleQuestion(faq.id)} className="flex items-center justify-between w-full p-6 bg-white hover:bg-gray-50 transition-colors text-left" data-id="element-2598">
                <h3 className="text-lg font-bold text-charcoal" data-id="element-2599">
                  {faq.question}
                </h3>
                {openQuestion === faq.id ? <MinusIcon className="h-5 w-5 text-teal flex-shrink-0" data-id="element-2600" /> : <PlusIcon className="h-5 w-5 text-teal flex-shrink-0" data-id="element-2601" />}
              </button>
              {openQuestion === faq.id && <div className="p-6 bg-gray-50 border-t border-gray-200" data-id="element-2602">
                  <p className="text-charcoal/80" data-id="element-2603">{faq.answer}</p>
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
};
const CtaSection = () => {
  return <section className="py-20 bg-gradient-to-br from-teal to-teal/90 relative overflow-hidden" data-id="element-2604">
      <div className="absolute inset-0 overflow-hidden" data-id="element-2605">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-white/10 blur-3xl" data-id="element-2606"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full bg-white/5 blur-3xl" data-id="element-2607"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2608">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2609">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-6" data-id="element-2610">
            <CreditCardIcon className="h-8 w-8 text-white" data-id="element-2611" />
          </div>
          <h2 className="text-4xl font-bold mb-6 text-white tracking-tight" data-id="element-2612">
            Get certified today with Kivarro's accredited HACCP Level 1 Food
            Safety Training
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-3xl mx-auto" data-id="element-2613">
            Protect your customers, comply with Irish law, and gain a recognised
            certificate — online, fast, and accessible anywhere.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center" data-id="element-2614">
            <a href="#individual-enrollment" className="px-8 py-4 bg-white text-teal font-medium rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg" data-id="element-2615">
              👉 Enroll Now (Individuals)
            </a>
            <a href="#group-enrollment" className="px-8 py-4 bg-transparent text-white font-medium rounded-lg border-2 border-white hover:bg-white/10 transition-all duration-300" data-id="element-2616">
              👉 Request Group Booking
            </a>
          </div>
        </div>
      </div>
    </section>;
};